import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Group,
  Button,
  UnstyledButton,
  Stack,
  Text,
  Menu,
  Box,
  Divider,
  Container,
  Drawer,
  Burger,
  rem,
  Indicator,
  TextInput,
  ActionIcon,
  HoverCard,
  Avatar,
  ScrollArea,
  Select,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import * as Icons from "@tabler/icons-react";

import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useTranslation } from "../context/TranslationContext";
import apiService from "../services/api";
import { formatPrice } from "../utils/formatPrice";
import { localizedField } from "../utils/localizedField";

export default function Navbar() {
  const { t, language, changeLanguage } = useTranslation();
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);

  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isBusiness } = useAuth();
  const { cartItems, removeFromCart, getCartCount, getCartTotal, vatExclusive } =
    useCart();

  useEffect(() => {
    apiService.getProductCategories().then(setCategories).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      closeDrawer();
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <Box
      component="nav"
      style={{
        height: rem(70),
        borderBottom: `${rem(1)} solid var(--mantine-color-gray-3)`,
        position: "sticky",
        top: 0,
        zIndex: 100,
        backgroundColor: "white",
      }}
    >
      <Container size="lg" h="100%">
        <Group justify="space-between" h="100%">
          {/* Logo */}
          <UnstyledButton component={Link} to="/">
            <Text
              fw={900}
              size="xl"
              variant="gradient"
              gradient={{ from: "blue", to: "cyan" }}
            >
              YOGI FOODS
            </Text>
          </UnstyledButton>

          {/* Desktop Menu */}
          <Group gap={5} visibleFrom="sm">
            <Button variant="subtle" component={Link} to="/" color="gray">
              {t("home", "Home")}
            </Button>

            {/* Products Menu - Available to ALL users */}
            <Menu
              trigger="hover"
              openDelay={100}
              closeDelay={400}
              withinPortal
            >
              <Menu.Target>
                <Button
                  variant="subtle"
                  color="gray"
                  rightSection={<Icons.IconChevronDown size={14} />}
                  onClick={() => navigate("/products")}
                >
                  {t("products", "Products")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {categories.map(cat => (
                  <Menu.Item
                    key={cat.id}
                    component={Link}
                    to={`/products?category=${encodeURIComponent(cat.name)}`}
                    leftSection={<Icons.IconPackage size={16} />}
                  >
                    {cat.name}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>

            {/* Quick Order Button - ONLY for business users (right after Products) */}
            {isBusiness && (
              <Button
                variant="subtle"
                component={Link}
                to="/bulk-order"
                color="gray"
              >
                {t("quickOrder", "Quick Order")}
              </Button>
            )}

            {/* Search Bar - Available to ALL users */}
            <form
              onSubmit={handleSearch}
              style={{ display: "flex", alignItems: "center" }}
            >
              <label className="sr-only" htmlFor="navbar-search-input">
                {t("searchProducts", "Search products")}
              </label>
              <TextInput
                id="navbar-search-input"
                placeholder={t("searchProducts", "Search...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                aria-label={t("searchProducts", "Search products")}
                rightSection={
                  <ActionIcon
                    size={24}
                    radius="xl"
                    variant="subtle"
                    type="submit"
                    aria-label={t("searchSubmit", "Submit search")}
                  >
                    <Icons.IconSearch size={16} stroke={1.5} aria-hidden="true" />
                  </ActionIcon>
                }
              />
            </form>

            {/* Cart Hover Card - Available to ALL authenticated users */}
            {isAuthenticated && (
              <HoverCard width={320} shadow="md" withArrow openDelay={200}>
                <HoverCard.Target>
                  <Indicator
                    label={getCartCount()}
                    size={16}
                    disabled={getCartCount() === 0}
                    offset={5}
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      component={Link}
                      to="/cart"
                    >
                      <Icons.IconShoppingCart size={22} />
                    </ActionIcon>
                  </Indicator>
                </HoverCard.Target>
                <HoverCard.Dropdown p="md">
                  {cartItems.length === 0 ? (
                    <Text size="sm" ta="center" c="dimmed">
                      {t("cartEmpty", "Your cart is empty")}
                    </Text>
                  ) : (
                    <Stack gap="xs">
                      <ScrollArea.Autosize maxHeight={300} mah={400}>
                        <Stack gap="sm">
                          {cartItems.map((item) => (
                            <Group
                              key={item.cartItemId}
                              wrap="nowrap"
                              align="center"
                              justify="space-between"
                            >
                              <Box w={40}>
                                <Avatar
                                  src={item.image}
                                  alt={localizedField(item, 'name', language)}
                                  radius="sm"
                                  size="md"
                                  styles={{
                                    image: { objectFit: "contain" },
                                  }}
                                />
                              </Box>
                              <Box style={{ flex: 1 }}>
                                <Text size="xs" fw={700} lineClamp={1}>
                                  {localizedField(item, 'name', language)}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {item.quantity} × {formatPrice(vatExclusive && item.netPrice != null ? item.netPrice : item.price)}
                                </Text>
                              </Box>
                              <Box w={24}>
                                <ActionIcon
                                  color="red"
                                  variant="subtle"
                                  size="sm"
                                  onClick={() =>
                                    removeFromCart(item.cartItemId)
                                  }
                                  aria-label={(() => {
                                    const itemName = localizedField(item, 'name', language) || "item";
                                    return t("removeFromCart", `Remove ${itemName} from cart`, { name: itemName });
                                  })()}
                                >
                                  <Icons.IconTrash size={16} stroke={1.5} aria-hidden="true" />
                                </ActionIcon>
                              </Box>
                            </Group>
                          ))}
                        </Stack>
                      </ScrollArea.Autosize>
                      <Divider />
                      <Group justify="space-between">
                        <Text size="sm" fw={700}>
                          {t("total", "Total")}:
                        </Text>
                        <Text size="sm" fw={700}>
                          {formatPrice(getCartTotal())}
                        </Text>
                      </Group>
                      <Button
                        fullWidth
                        size="xs"
                        component={Link}
                        to="/cart"
                        onClick={closeDrawer}
                      >
                        {t("viewCart", "View Cart")}
                      </Button>
                    </Stack>
                  )}
                </HoverCard.Dropdown>
              </HoverCard>
            )}
          </Group>

          {/* User & Settings Group */}
          <Group gap="xs" visibleFrom="sm">
            <Select
              size="xs"
              w={100}
              data={[
                { value: "en", label: "English" },
                { value: "de", label: "Deutsch" },
              ]}
              value={language}
              onChange={changeLanguage}
            />

            {isAuthenticated ? (
              <Menu position="bottom-end" withArrow shadow="md">
                <Menu.Target>
                  <UnstyledButton>
                    <Group gap="xs">
                      <Avatar color="blue" radius="xl" size="sm">
                        <span aria-hidden="true">{user?.name?.charAt(0)}</span>
                      </Avatar>
                      <Text size="sm" fw={500}>
                        {user?.name || "Account"}
                      </Text>
                      <Icons.IconChevronDown size={14} aria-hidden="true" />
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{user?.email}</Menu.Label>
                  <Menu.Item
                    component={Link}
                    to="/dashboard/settings"
                    leftSection={<Icons.IconSettings size={16} />}
                  >
                    {t("myAccount", "My Account")}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<Icons.IconLogout size={16} />}
                    onClick={handleLogout}
                  >
                    {t("logout", "Logout")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : (
              <Button component={Link} to="/login" variant="filled" size="sm">
                {t("login", "Login")}
              </Button>
            )}
          </Group>            {/* Mobile Cart + Burger */}
            <Group gap="xs" hiddenFrom="sm">
              {isAuthenticated && (
                <Indicator
                  label={getCartCount()}
                  size={16}
                  disabled={getCartCount() === 0}
                  offset={5}
                >
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="lg"
                    component={Link}
                    to="/cart"
                    aria-label={t("cartAriaLabel", "Shopping cart, {count} items", {
                      count: getCartCount(),
                    })}
                  >
                    <Icons.IconShoppingCart size={22} aria-hidden="true" />
                  </ActionIcon>
                </Indicator>
              )}
              <Burger
                opened={drawerOpened}
                onClick={toggleDrawer}
                size="sm"
                aria-label={drawerOpened ? t("closeMenu", "Close menu") : t("openMenu", "Open menu")}
                aria-expanded={drawerOpened}
                aria-controls="mobile-nav-drawer"
              />
            </Group>
        </Group>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        id="mobile-nav-drawer"
        opened={drawerOpened}
        onClose={closeDrawer}
        size="100%"
        padding="md"
        title={t("mainMenu", "Main menu")}
        hiddenFrom="sm"
        zIndex={1000}
      >
        <Stack gap="md" px="md" style={{ height: `calc(100vh - ${rem(80)})`, display: "flex", flexDirection: "column" }}>
          <Divider my="sm" />
          <ScrollArea style={{ flex: 1 }}>
            <Stack gap="md">
              <form onSubmit={handleSearch}>
                <label className="sr-only" htmlFor="mobile-navbar-search-input">
                  {t("searchProducts", "Search products")}
                </label>
                <TextInput
                  id="mobile-navbar-search-input"
                  placeholder={t("searchProducts", "Search products…")}
                  leftSection={<Icons.IconSearch size={16} aria-hidden="true" />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  aria-label={t("searchProducts", "Search products")}
                />
              </form>

              <Button
                variant="light"
                fullWidth
                component={Link}
                to="/"
                onClick={closeDrawer}
              >
                {t("home", "Home")}
              </Button>
              <Button
                variant="light"
                fullWidth
                component={Link}
                to="/products"
                onClick={closeDrawer}
              >
                {t("products", "Products")}
              </Button>

              {/* Quick Order - ONLY for business users */}
              {isBusiness && isAuthenticated && (
                <Button
                  variant="light"
                  fullWidth
                  component={Link}
                  to="/bulk-order"
                  onClick={closeDrawer}
                  leftSection={<Icons.IconBolt size={18} />}
                >
                  {t("quickOrder", "Quick Order")}
                </Button>
              )}

              {isAuthenticated && (
                <Button
                  variant="light"
                  fullWidth
                  component={Link}
                  to="/cart"
                  onClick={closeDrawer}
                  leftSection={<Icons.IconShoppingCart size={18} />}
                >
                  {t("viewCart", "Cart")} ({getCartCount()})
                </Button>
              )}
            </Stack>
          </ScrollArea>

          <Divider label="Settings" labelPosition="center" />

          <Text size="sm" fw={500} mb={4}>Language</Text>
          <select
            value={language}
            onChange={(e) => changeLanguage(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid var(--mantine-color-gray-4)",
              fontSize: "14px",
              backgroundColor: "white",
            }}
          >
            <option value="en">English</option>
            <option value="de">Deutsch</option>
          </select>

          {isAuthenticated && (
            <Button
              variant="light"
              fullWidth
              component={Link}
              to="/dashboard/settings"
              onClick={closeDrawer}
              leftSection={<Icons.IconSettings size={18} />}
            >
              {t("myAccount", "My Account")}
            </Button>
          )}

          {isAuthenticated ? (
            <Button variant="outline" fullWidth onClick={handleLogout}>
              {t("logout", "Logout")}
            </Button>
          ) : (
            <Button
              fullWidth
              component={Link}
              to="/login"
              onClick={closeDrawer}
            >
              {t("login", "Login")}
            </Button>
          )}
        </Stack>
      </Drawer>
    </Box>
  );
}
